import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Loader2, MapPin, Search, X } from 'lucide-react';
import { Input } from '@ci-ville/ui';
import { api } from '@/lib/api-client';
/** Recherche d'adresse via le serveur (MapTiler en production, Nominatim en développement). */
export function AddressSearch({ tenantId, onSelect, placeholder = 'Rechercher une adresse…', className, inputClassName }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const boxRef = useRef(null);
    const seq = useRef(0);
    const chosenLabel = useRef(null);
    useEffect(() => {
        const q = query.trim();
        // Le libellé d'une adresse choisie ne relance pas de recherche
        if (chosenLabel.current === query)
            return;
        chosenLabel.current = null;
        if (q.length < 3) {
            setResults([]);
            return;
        }
        const id = ++seq.current;
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const r = await api.geo.search(q, tenantId);
                if (id === seq.current) {
                    setResults(r);
                    setActive(0);
                    setOpen(true);
                }
            }
            catch {
                if (id === seq.current)
                    setResults([]);
            }
            finally {
                if (id === seq.current)
                    setLoading(false);
            }
        }, 450);
        return () => clearTimeout(t);
    }, [query, tenantId]);
    useEffect(() => {
        const handler = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target))
                setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);
    const choose = (r) => {
        seq.current++;
        chosenLabel.current = r.label;
        setQuery(r.label);
        setOpen(false);
        onSelect(r);
    };
    return (_jsxs("div", { ref: boxRef, className: `relative ${className ?? ''}`, children: [_jsx(Search, { className: "pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" }), _jsx(Input, { value: query, onChange: (e) => setQuery(e.target.value), onFocus: () => results.length && setOpen(true), onKeyDown: (e) => {
                    if (!open || results.length === 0)
                        return;
                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setActive((a) => Math.min(a + 1, results.length - 1));
                    }
                    if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setActive((a) => Math.max(a - 1, 0));
                    }
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        choose(results[active]);
                    }
                    if (e.key === 'Escape')
                        setOpen(false);
                }, placeholder: placeholder, className: `pl-9 pr-8 ${inputClassName ?? ''}`, role: "combobox", "aria-expanded": open, "aria-autocomplete": "list" }), _jsx("div", { className: "absolute right-2.5 top-1/2 -translate-y-1/2", children: loading ? (_jsx(Loader2, { className: "size-4 animate-spin text-muted-foreground" })) : query ? (_jsx("button", { type: "button", onClick: () => { setQuery(''); setResults([]); }, className: "text-muted-foreground hover:text-foreground", title: "Effacer", children: _jsx(X, { className: "size-4" }) })) : null }), open && (_jsx("ul", { className: "absolute left-0 right-0 top-full z-[1300] mt-1 max-h-72 overflow-y-auto rounded-md border border-border bg-card py-1 text-foreground shadow-xl", role: "listbox", children: results.length === 0 ? (_jsx("li", { className: "px-3 py-2 text-xs text-muted-foreground", children: "Aucune adresse trouv\u00E9e" })) : (results.map((r, i) => (_jsx("li", { role: "option", "aria-selected": i === active, children: _jsxs("button", { type: "button", onMouseEnter: () => setActive(i), onClick: () => choose(r), className: `flex w-full items-start gap-2 px-3 py-2 text-left text-xs ${i === active ? 'bg-muted' : ''}`, children: [_jsx(MapPin, { className: "mt-0.5 size-3.5 shrink-0 text-brand" }), _jsx("span", { className: "line-clamp-2", children: r.label })] }) }, `${r.latitude},${r.longitude},${i}`)))) }))] }));
}
//# sourceMappingURL=AddressSearch.js.map